/**
 * F-073 — Supabase singleton client.
 *
 * Returns a configured client when both `VITE_SUPABASE_URL` and
 * `VITE_SUPABASE_ANON_KEY` are present, otherwise `null`. Callers branch
 * on `null` to fall back to the offline (localStorage) leaderboard.
 *
 * The signing-key reader is split so tests can configure HMAC behavior
 * independently from the wire client.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

let cached: SupabaseClient | null | undefined = undefined;

interface EnvLike {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_SCORE_SIGNING_KEY?: string;
}

let envOverride: EnvLike | null = null;

function readEnv(): EnvLike {
  if (envOverride) return envOverride;
  // Vite injects import.meta.env; vitest provides the same shape.
  const meta = (import.meta as unknown as { env?: EnvLike }).env;
  return meta ?? {};
}

/** Test-only — inject an env shape and reset cached client. */
export function __setEnvForTest(env: EnvLike | null): void {
  envOverride = env;
  cached = undefined;
}

export function getSupabaseClient(): SupabaseClient | null {
  if (cached !== undefined) return cached;
  const env = readEnv();
  if (!env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY) {
    cached = null;
    return cached;
  }
  cached = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return cached;
}

export function getScoreSigningKey(): string | null {
  return readEnv().VITE_SCORE_SIGNING_KEY ?? null;
}

/** Test-only — clear the singleton so a fresh env can be picked up. */
export function __resetSupabaseClient(): void {
  cached = undefined;
}

/** True when both URL and anon key are present (auth/leaderboard available). */
export function isOnlineModeAvailable(): boolean {
  return getSupabaseClient() !== null;
}
